/**
 * Batching + retrying queue for proctor events.
 *
 * Guarantees that matter here:
 *   - No event is dropped because the network blipped. Failed flushes go back
 *     to the head of the queue and are retried with backoff.
 *   - The queue is mirrored into sessionStorage on every change, so an
 *     accidental reload mid-exam does not lose the events that proved what
 *     happened. It drains on the next flush.
 *   - A final best-effort flush runs on pagehide via sendBeacon, which is the
 *     only transport the browser guarantees during unload.
 *
 * This module never blocks the exam and never throws into the UI.
 */
import type { ProctorEventInput, ProctorEventType } from "@exam/shared";
import { PROCTOR_EVENT_SEVERITY } from "@exam/shared";
import { sendProctorEvents } from "@/lib/exam-api";
import type { ServerClock } from "@/lib/exam/serverClock";

const FLUSH_INTERVAL_MS = 5_000;
const MAX_BATCH = 100;
const MAX_QUEUE = 1_000;
const BACKOFF_MS = [1_000, 2_000, 5_000, 10_000, 30_000];

function storageKey(attemptId: string) {
  return `ep:proctor:pending:${attemptId}`;
}

export interface ProctorQueueOptions {
  attemptId: string;
  /**
   * Server-anchored clock. Event timestamps come from this, never from
   * `Date.now()` — a laptop whose clock is minutes off (or is changed mid-exam)
   * would otherwise scatter events across the admin's timeline.
   */
  clock: ServerClock;
  /** `attempts.started_at` — the master timeline origin (t=0). */
  startedAtMs: number | null;
  /** Set on the phone; omitted on the laptop, which uses the session cookie. */
  deviceToken?: string;
  /** Notified after every successful or failed flush, for the warning banner. */
  onChange?: (state: ProctorQueueState) => void;
}

export interface ProctorQueueState {
  /** Events recorded this session, by type. Drives the violation counter. */
  counts: Record<string, number>;
  totalViolations: number;
  pending: number;
  /** True when at least one flush has failed and we are backing off. */
  degraded: boolean;
  lastEvent: { type: ProctorEventType; ts_ms: number } | null;
}

/**
 * Events that count toward the number shown to the student. NETWORK_DROP and
 * WINDOW_BLUR fire often and benignly (an OS notification steals focus), so
 * they are logged but not counted — a scary number that is mostly noise trains
 * the student to ignore the banner.
 */
const COUNTED: ReadonlySet<ProctorEventType> = new Set<ProctorEventType>([
  "TAB_SWITCH",
  "FULLSCREEN_EXIT",
  "COPY_ATTEMPT",
  "PASTE_ATTEMPT",
  "WEBCAM_STOPPED",
  "PHONE_DISCONNECTED",
  "MULTI_FACE",
]);

export class ProctorQueue {
  private readonly attemptId: string;
  private readonly clock: ServerClock;
  private readonly deviceToken?: string;
  private readonly onChange?: (state: ProctorQueueState) => void;
  private startedAtMs: number | null;

  private queue: ProctorEventInput[] = [];
  private counts: Record<string, number> = {};
  private lastEvent: ProctorQueueState["lastEvent"] = null;
  private failures = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private flushing = false;
  private stopped = false;

  constructor(options: ProctorQueueOptions) {
    this.attemptId = options.attemptId;
    this.clock = options.clock;
    this.startedAtMs = options.startedAtMs;
    this.deviceToken = options.deviceToken;
    this.onChange = options.onChange;
    this.restore();
    this.schedule(FLUSH_INTERVAL_MS);
  }

  /** `started_at` is null until the attempt starts; set it once it is known. */
  setStartedAt(startedAtMs: number | null): void {
    this.startedAtMs = startedAtMs;
  }

  /** Record an event. Cheap and synchronous — safe to call from a DOM handler. */
  record(
    type: ProctorEventType,
    metadata?: Record<string, unknown>,
  ): void {
    if (this.stopped) return;

    // `ts_ms` is absolute epoch ms, as docs/API.md §4 requires — but read off
    // the SERVER-anchored clock, so the `offset_ms` the API derives from
    // `attempts.started_at` lands on the right spot in the admin's timeline.
    // `offset_ms` is duplicated into metadata so the recording player has the
    // master-timeline position directly, without re-deriving it.
    const nowMs = Math.round(this.clock.now());
    const event: ProctorEventInput = {
      ts_ms: nowMs,
      type,
      severity: PROCTOR_EVENT_SEVERITY[type],
      metadata: {
        ...(metadata ?? {}),
        offset_ms: this.clock.offsetFrom(this.startedAtMs),
        clock_synced: this.clock.isSynced,
      },
    };

    this.queue.push(event);
    if (this.queue.length > MAX_QUEUE) {
      // Keep the newest — the oldest are already on the server in almost every
      // case, and an unbounded array in a 3-hour exam is its own failure mode.
      this.queue = this.queue.slice(-MAX_QUEUE);
    }

    this.counts[type] = (this.counts[type] ?? 0) + 1;
    this.lastEvent = { type, ts_ms: event.ts_ms };

    this.persist();
    this.emit();

    // Critical events shouldn't wait up to 5s to reach the server.
    if (event.severity === "critical") {
      void this.flush();
    } else if (this.timer === null) {
      // Make sure a flush is actually pending. Without this, an event recorded
      // while no timer is armed would sit in the queue indefinitely.
      this.schedule(FLUSH_INTERVAL_MS);
    }
  }

  get state(): ProctorQueueState {
    let totalViolations = 0;
    for (const [type, n] of Object.entries(this.counts)) {
      if (COUNTED.has(type as ProctorEventType)) totalViolations += n;
    }
    return {
      counts: { ...this.counts },
      totalViolations,
      pending: this.queue.length,
      degraded: this.failures > 0,
      lastEvent: this.lastEvent,
    };
  }

  /** Sends everything queued. Never rejects. */
  async flush(): Promise<void> {
    if (this.flushing) return;

    // An empty queue must still re-arm the timer. Returning without doing so
    // ends the flush chain, and every later non-critical event (WINDOW_BLUR,
    // NETWORK_DROP) is then stranded in the queue until something critical
    // happens to kick it — which may be never.
    if (this.queue.length === 0) {
      if (!this.stopped) this.schedule(FLUSH_INTERVAL_MS);
      return;
    }

    this.flushing = true;

    const batch = this.queue.slice(0, MAX_BATCH);

    try {
      await sendProctorEvents(this.attemptId, batch, this.deviceToken);
      this.queue = this.queue.slice(batch.length);
      this.failures = 0;
      this.persist();
    } catch {
      // Put nothing back — `batch` was never removed. Just back off.
      this.failures += 1;
    } finally {
      this.flushing = false;
      this.emit();
      if (!this.stopped) {
        const backoff =
          BACKOFF_MS[Math.min(this.failures - 1, BACKOFF_MS.length - 1)] ??
          FLUSH_INTERVAL_MS;
        this.schedule(this.failures === 0 ? FLUSH_INTERVAL_MS : backoff);
      }
    }
  }

  /**
   * Last-gasp flush during unload. `fetch` is cancelled when the page dies;
   * sendBeacon is queued by the browser and survives.
   */
  flushOnUnload(): void {
    // sendBeacon cannot set X-Device-Token, so the phone has nothing to gain
    // here; its events survive in sessionStorage and flush on reconnect.
    if (this.queue.length === 0 || this.deviceToken) return;
    try {
      const body = new Blob(
        [JSON.stringify({ events: this.queue.slice(0, MAX_BATCH) })],
        { type: "application/json" },
      );
      navigator.sendBeacon(`/api/proctor/${this.attemptId}/events`, body);
    } catch {
      /* nothing left to try */
    }
  }

  async stop(): Promise<void> {
    this.stopped = true;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    await this.flush();
  }

  /* -- internals ---------------------------------------------------- */

  private schedule(ms: number): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      // Cleared before flushing so `timer === null` reliably means "no flush
      // is pending" — which is what `record` checks to re-arm the chain.
      this.timer = null;
      void this.flush();
    }, ms);
  }

  private emit(): void {
    this.onChange?.(this.state);
  }

  private persist(): void {
    try {
      if (this.queue.length === 0) {
        sessionStorage.removeItem(storageKey(this.attemptId));
      } else {
        sessionStorage.setItem(
          storageKey(this.attemptId),
          JSON.stringify(this.queue),
        );
      }
    } catch {
      /* storage full or blocked — in-memory queue still works */
    }
  }

  private restore(): void {
    try {
      const raw = sessionStorage.getItem(storageKey(this.attemptId));
      if (!raw) return;
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        this.queue = parsed as ProctorEventInput[];
      }
    } catch {
      /* ignore corrupt state */
    }
  }
}
